import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Store, Package, Activity, Sparkles, ArrowRight, ExternalLink, Plus } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthProvider";
import { useStore } from "@/context/StoreProvider";
import AiScore from "@/components/common/AiScore";

function Card({ children, className = "", ...props }) {
  return <div className={`rounded-lg border border-border bg-card p-5 ${className}`} {...props}>{children}</div>;
}

export default function DashboardHome() {
  const t = useT();
  const { user } = useAuth();
  const { store, loading } = useStore();
  const [summary, setSummary] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (store) {
      api.get(`/stores/${store.id}/summary`).then(({ data }) => setSummary(data)).catch(() => {});
    }
  }, [store]);

  if (loading) return <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />;

  if (!store) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center" data-testid="no-store-cta">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand/10">
          <Store className="h-6 w-6 text-brand" />
        </div>
        <h1 className="mt-6 font-display text-2xl font-extrabold tracking-tighter">{t("dashboard.home.noStore")}</h1>
        <Link to="/dashboard/store"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 font-medium text-primary-foreground transition-transform hover:opacity-90 active:scale-95">
          <Plus className="h-4 w-4" />{t("dashboard.home.noStoreCta")}
        </Link>
      </div>
    );
  }

  const published = store.status === "published";
  const lastSync = summary?.last_sync ? new Date(summary.last_sync).toLocaleString() : t("dashboard.home.never");

  const cards = [
    {
      key: "status", icon: Store, label: t("dashboard.home.storeStatus"),
      content: (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-medium ${published ? "bg-success/10 text-success" : "bg-warning/10 text-warning"}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${published ? "bg-success" : "bg-warning"}`} />
          {published ? t("dashboard.home.published") : t("dashboard.home.draft")}
        </span>
      ),
    },
    {
      key: "products", icon: Package, label: t("dashboard.home.productCount"),
      content: <span className="font-mono text-3xl font-bold">{summary?.product_count ?? 0}</span>,
      hint: t("dashboard.home.aiIndexed", { count: summary?.ai_indexed_count ?? 0 }),
    },
    {
      key: "sync", icon: Activity, label: t("dashboard.home.lastSync"),
      content: <span className="text-sm font-medium">{lastSync}</span>,
    },
  ];

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter">{t("dashboard.home.title")}</h1>
        <p className="mt-1 text-muted-foreground">{t("dashboard.home.welcome", { name: user?.name || "" })}</p>
      </motion.div>

      <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* AI Score featured card */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="row-span-2 flex flex-col items-center justify-center rounded-lg border border-border bg-primary p-6 text-primary-foreground lg:col-span-1">
          <span className="text-xs uppercase tracking-widest text-primary-foreground/60">{t("dashboard.home.aiScore")}</span>
          <div className="my-4"><AiScore score={summary?.avg_ai_score ?? 0} size="lg" /></div>
          <p className="text-center text-xs text-primary-foreground/60">{t("dashboard.home.aiScoreHint")}</p>
        </motion.div>

        {cards.map((c, i) => (
          <motion.div key={c.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 * (i + 1) }}>
            <Card data-testid={`card-${c.key}`}>
              <div className="flex items-center gap-2 text-muted-foreground"><c.icon className="h-4 w-4" /><span className="text-xs uppercase tracking-wide">{c.label}</span></div>
              <div className="mt-3">{c.content}</div>
              {c.hint && <p className="mt-2 text-xs text-muted-foreground">{c.hint}</p>}
            </Card>
          </motion.div>
        ))}

        {/* Quick actions */}
        <Card className="md:col-span-2 lg:col-span-3">
          <h3 className="font-display text-sm font-bold uppercase tracking-wide text-muted-foreground">{t("dashboard.home.quickActions")}</h3>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <button onClick={() => navigate("/dashboard/products")} data-testid="qa-add-product"
              className="flex items-center justify-between rounded-md border border-border p-4 text-left transition-colors hover:bg-secondary/60">
              <span className="flex items-center gap-2 text-sm font-medium"><Plus className="h-4 w-4 text-brand" />{t("dashboard.home.addProduct")}</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </button>
            <button onClick={() => navigate("/dashboard/ai")} data-testid="qa-run-ai"
              className="flex items-center justify-between rounded-md border border-border p-4 text-left transition-colors hover:bg-secondary/60">
              <span className="flex items-center gap-2 text-sm font-medium"><Sparkles className="h-4 w-4 text-brand" />{t("dashboard.home.runAi")}</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </button>
            <a href={`/store/${store.id}`} target="_blank" rel="noreferrer" data-testid="qa-view-store"
              className="flex items-center justify-between rounded-md border border-border p-4 text-left transition-colors hover:bg-secondary/60">
              <span className="flex items-center gap-2 text-sm font-medium"><Store className="h-4 w-4 text-brand" />{t("dashboard.home.viewStore")}</span>
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </a>
          </div>
        </Card>
      </div>
    </div>
  );
}
